import { useState, useEffect, useRef, useCallback } from 'react';

interface UseAnimatedVisibilityOptions {
  /** Duration in ms - should match CSS transition duration */
  duration?: number;
  /** Callback when exit animation completes */
  onExitComplete?: () => void;
  /** Gate animation until DOM is ready (prevents animation skip on initial mount) */
  domReady?: boolean;
}

interface UseAnimatedVisibilityReturn {
  /** Whether component should be rendered in DOM */
  shouldRender: boolean;
  /** Whether component should show "open" animation state */
  isVisible: boolean;
  /** Call this to manually start close animation */
  startExit: () => void;
}

/**
 * Hook that manages mount/unmount timing for CSS exit animations.
 *
 * When `open` becomes true:
 * - `shouldRender` becomes true immediately (mount component)
 * - `isVisible` becomes true on next frame (trigger enter animation)
 *
 * When `open` becomes false:
 * - `isVisible` becomes false immediately (trigger exit animation)
 * - `shouldRender` becomes false after duration (unmount component)
 */
export function useAnimatedVisibility(
  open: boolean,
  options: UseAnimatedVisibilityOptions = {},
): UseAnimatedVisibilityReturn {
  const { duration = 300, onExitComplete, domReady = true } = options;

  const [shouldRender, setShouldRender] = useState(open);
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const pendingRafs = useRef<Set<number>>(new Set());
  const isExitingRef = useRef(false);

  // Handle open state changes
  useEffect(() => {
    if (open) {
      // Opening: render immediately, then trigger animation on next frame
      setShouldRender(true);
      isExitingRef.current = false;

      // Clear any pending exit timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = undefined;
      }

      // Only schedule RAF when domReady is true
      // This prevents animation skip on initial mount when DOM isn't ready yet
      if (domReady) {
        // Use double requestAnimationFrame to ensure DOM is ready before animation
        const id1 = requestAnimationFrame(() => {
          pendingRafs.current.delete(id1);
          const id2 = requestAnimationFrame(() => {
            pendingRafs.current.delete(id2);
            setIsVisible(true);
          });
          pendingRafs.current.add(id2);
        });
        pendingRafs.current.add(id1);
      }
    } else if (shouldRender && !isExitingRef.current) {
      // Closing: start exit animation
      isExitingRef.current = true;
      setIsVisible(false);

      // Unmount after animation duration
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false);
        isExitingRef.current = false;
        onExitComplete?.();
      }, duration);
    }
  }, [open, shouldRender, duration, onExitComplete, domReady]);

  // Trigger animation when domReady becomes true after initial mount
  // This handles the case where open=true but domReady=false initially
  useEffect(() => {
    if (open && shouldRender && !isVisible && domReady && !isExitingRef.current) {
      const id1 = requestAnimationFrame(() => {
        pendingRafs.current.delete(id1);
        const id2 = requestAnimationFrame(() => {
          pendingRafs.current.delete(id2);
          setIsVisible(true);
        });
        pendingRafs.current.add(id2);
      });
      pendingRafs.current.add(id1);
    }
  }, [domReady, open, shouldRender, isVisible]);

  // Cleanup timeouts and RAF on unmount
  useEffect(() => {
    const rafs = pendingRafs.current;
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      rafs.forEach(cancelAnimationFrame);
      rafs.clear();
    };
  }, []);

  const startExit = useCallback(() => {
    if (!isExitingRef.current && shouldRender) {
      isExitingRef.current = true;
      setIsVisible(false);
      timeoutRef.current = setTimeout(() => {
        setShouldRender(false);
        isExitingRef.current = false;
        onExitComplete?.();
      }, duration);
    }
  }, [duration, onExitComplete, shouldRender]);

  return { shouldRender, isVisible, startExit };
}
